import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppView } from '@/contexts/AppViewContext';
import { STANDARD_APP_HOME } from '@/lib/appAccess';

export function RequireAppAccess({ children }: { children: ReactNode }) {
  const { canAccessPath, accessLoading, accessError, refreshAccess } = useAppView();
  const location = useLocation();
  const allowed = canAccessPath(location.pathname);
  // '/' is the app root, not a page someone chose. Standard users are simply
  // sent to their home instead of being told they were denied something.
  const isImplicitLanding = location.pathname === '/';

  useEffect(() => {
    if (!accessLoading && !accessError && !allowed && !isImplicitLanding) {
      toast('You do not have access to that page.', {
        id: 'standard-view-route-redirect',
        icon: '🛡️',
      });
    }
  }, [allowed, isImplicitLanding, accessLoading, accessError]);

  if (accessLoading) return <p role="status" className="p-8">Loading workspace access…</p>;
  if (accessError) return <div role="alert" className="p-8">Unable to load workspace access. <button className="underline" onClick={() => void refreshAccess()}>Retry</button></div>;

  if (!allowed) {
    const home = canAccessPath(STANDARD_APP_HOME) ? STANDARD_APP_HOME : '/';
    if (location.pathname === home) return <p className="p-8">No workspace access is assigned. Contact an administrator.</p>;
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
